import React from 'react';
import { motion } from 'framer-motion';
import { HardHat, Mail, MapPin, Sparkles, Clock, Sun, Moon } from 'lucide-react';
import { Logo } from '../components/Logo';
import { SEO } from '../components/SEO';
import { SITE_EMAIL, SITE_ADDRESS, SITE_NAME } from '../config/site';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';

const fadeInUp = {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' },
};

const stagger = {
    animate: { transition: { staggerChildren: 0.12 } },
};

function AmbientBackground() {
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-cyan-500/10" />
            <motion.div
                className="absolute -top-24 -left-16 w-80 h-80 rounded-full bg-primary/20 blur-3xl"
                animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1, 1.12, 1] }}
                transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
                className="absolute top-1/3 -right-20 w-96 h-96 rounded-full bg-cyan-500/15 blur-3xl"
                animate={{ x: [0, -40, 0], y: [0, 28, 0], scale: [1, 1.18, 1] }}
                transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
                className="absolute -bottom-20 left-1/3 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl"
                animate={{ x: [0, 36, 0], y: [0, -24, 0] }}
                transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }}
            />
            <div
                className="absolute inset-0 opacity-[0.04]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(31, 157, 85, 0.45) 1px, transparent 1px), linear-gradient(90deg, rgba(31, 157, 85, 0.45) 1px, transparent 1px)',
                    backgroundSize: '48px 48px',
                }}
            />
        </div>
    );
}

export default function MaintenancePage() {
    const { theme, toggleTheme } = useTheme();

    return (
        <div className="relative min-h-screen flex flex-col overflow-hidden bg-background">
            <SEO
                title="Under Construction"
                description={`${SITE_NAME} portal is currently under construction. Check back soon or email ${SITE_EMAIL}.`}
                noindex
                includeGlobalSchema={false}
            />

            <AmbientBackground />

            <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
                <Logo size="default" />
                <Button
                    variant="outline"
                    size="icon"
                    onClick={toggleTheme}
                    aria-label="Toggle theme"
                    className="rounded-full border-border/80"
                    data-testid="maintenance-theme-toggle"
                >
                    {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </Button>
            </header>

            <main className="relative z-10 flex-1 flex items-center justify-center px-6 md:px-12 py-12">
                <motion.div
                    initial="initial"
                    animate="animate"
                    variants={stagger}
                    className="w-full max-w-2xl text-center"
                >
                    <motion.div
                        variants={fadeInUp}
                        className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-primary to-emerald-500 shadow-lg shadow-primary/30"
                    >
                        <HardHat className="h-10 w-10 text-white" strokeWidth={1.75} />
                    </motion.div>

                    <motion.div
                        variants={fadeInUp}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
                    >
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">Portal Under Construction</span>
                    </motion.div>

                    <motion.h1
                        variants={fadeInUp}
                        className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-tight"
                    >
                        We&apos;re building something{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-emerald-400 to-cyan-400">
                            intelligent
                        </span>
                    </motion.h1>

                    <motion.p
                        variants={fadeInUp}
                        className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl mx-auto"
                    >
                        The {SITE_NAME} portal is getting a refresh. We&apos;ll be back shortly with AI solutions
                        for Philippine businesses.
                    </motion.p>

                    <motion.div variants={fadeInUp} className="mt-10 max-w-md mx-auto">
                        <div className="flex items-center justify-between text-xs font-medium text-muted-foreground mb-2">
                            <span className="inline-flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5" />
                                Progress
                            </span>
                            <span>Coming soon</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                            <motion.div
                                className="h-full rounded-full bg-gradient-to-r from-primary to-cyan-400"
                                initial={{ width: '0%' }}
                                animate={{ width: '68%' }}
                                transition={{ duration: 1.4, delay: 0.4, ease: 'easeOut' }}
                            />
                        </div>
                    </motion.div>

                    <motion.div
                        variants={fadeInUp}
                        className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
                    >
                        <Button
                            asChild
                            size="lg"
                            className="rounded-full px-8 shadow-lg shadow-primary/30 bg-gradient-to-r from-primary to-emerald-500"
                            data-testid="maintenance-email-btn"
                        >
                            <a href={`mailto:${SITE_EMAIL}`}>
                                <Mail className="mr-2 h-5 w-5" />
                                Contact Us
                            </a>
                        </Button>
                    </motion.div>

                    <motion.div
                        variants={fadeInUp}
                        className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground"
                    >
                        <a
                            href={`mailto:${SITE_EMAIL}`}
                            className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
                        >
                            <Mail className="h-4 w-4 text-primary" />
                            {SITE_EMAIL}
                        </a>
                        <span className="hidden sm:inline text-border">•</span>
                        <span className="inline-flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-primary" />
                            {SITE_ADDRESS}
                        </span>
                    </motion.div>
                </motion.div>
            </main>

            <footer className="relative z-10 px-6 md:px-12 py-6 text-center text-sm text-muted-foreground">
                © {new Date().getFullYear()} {SITE_NAME}. All rights reserved.
            </footer>
        </div>
    );
}
